import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MoreHorizontal } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { PurgeMasterDialog } from '@/components/efs/PurgeMasterDialog';

import { FacilitiesFormDialog } from './FacilitiesFormDialog';
import { useActivateFacility, useDeactivateFacility, usePurgeFacility } from './useFacilities';
import type { Facility } from './types';

interface FacilitiesRowActionsProps {
  row: Facility;
}

export const FacilitiesRowActions = ({ row }: FacilitiesRowActionsProps) => {
  const { t } = useTranslation();
  const deactivate = useDeactivateFacility();
  const activate = useActivateFacility();
  const purge = usePurgeFacility();
  const [editOpen, setEditOpen] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('efs.settings.facilities.actions.row_menu', { ns: 'settings' })}
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => setEditOpen(true)}>
            {t('efs.settings.facilities.actions.edit', { ns: 'settings' })}
          </DropdownMenuItem>
          {row.active_flag ? (
            <DropdownMenuItem
              onSelect={() => deactivate.mutate(row.facility_id)}
              disabled={deactivate.isPending}
            >
              {t('efs.settings.facilities.actions.deactivate', { ns: 'settings' })}
            </DropdownMenuItem>
          ) : (
            <>
              <DropdownMenuItem
                onSelect={() => activate.mutate(row.facility_id)}
                disabled={activate.isPending}
              >
                {t('efs.settings.facilities.actions.activate', { ns: 'settings' })}
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => {
                  purge.reset();
                  setPurgeOpen(true);
                }}
                className="text-red-900 focus:text-red-900"
              >
                {t('efs.masters.actions.purge', { ns: 'masters' })}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <PurgeMasterDialog
        open={purgeOpen}
        onOpenChange={setPurgeOpen}
        name={row.facility_name}
        isPending={purge.isPending}
        error={purge.error}
        onConfirm={() => purge.mutate(row.facility_id, { onSuccess: () => setPurgeOpen(false) })}
      />

      <FacilitiesFormDialog open={editOpen} onOpenChange={setEditOpen} row={row} />
    </>
  );
};
